package com.timacookies

import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ScrollBehavior
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.TextTransform
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.letterSpacing
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.scrollBehavior
import com.varabyte.kobweb.compose.ui.modifiers.setVariable
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.textTransform
import com.varabyte.kobweb.compose.ui.modifiers.translateY
import com.varabyte.kobweb.silk.components.forms.ButtonStyle
import com.varabyte.kobweb.silk.components.forms.ButtonVars
import com.varabyte.kobweb.silk.components.layout.HorizontalDividerStyle
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.init.registerStyleBase
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.addVariantBase
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import com.varabyte.kobweb.silk.theme.modifyStyleBase
import org.jetbrains.compose.web.css.CSSMediaQuery
import org.jetbrains.compose.web.css.StylePropertyValue
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px

@InitSilk
fun initSiteStyles(ctx: InitSilkContext) {
  // This site does not need scrolling itself, but this is a good demonstration for how you might enable this in your
  // own site. Note that we only enable smooth scrolling unless the user has requested reduced motion, which is
  // considered a best practice.
  ctx.stylesheet.registerStyle("html") {
    cssRule(
      CSSMediaQuery.MediaFeature(
        "prefers-reduced-motion",
        StylePropertyValue("no-preference"),
      ),
    ) {
      Modifier.scrollBehavior(ScrollBehavior.Smooth)
    }
  }

  ctx.stylesheet.registerStyleBase("body") {
    Modifier
      .fontFamily("Montserrat", "sans-serif")
      .fontSize(16.px)
      .lineHeight(1.8)
      .letterSpacing(0.05.cssRem)
  }

  // Silk dividers only extend 90% by default; we want full width dividers in our site
  ctx.theme.modifyStyleBase(HorizontalDividerStyle) {
    Modifier.fillMaxWidth().opacity(0.1)
  }
}

val FadeInAnimation = Keyframes {
  from { Modifier.opacity(0).translateY(20.px) }
  to { Modifier.opacity(1).translateY(0.px) }
}

val HeadlineTextStyle = CssStyle {
  base {
    Modifier
      .fontFamily("Noto Serif Display", "serif")
      .fontSize(2.2.cssRem)
      .textAlign(TextAlign.Start)
      .lineHeight(1.1)
      .letterSpacing(0.02.cssRem)
  }
  Breakpoint.MD {
    Modifier.fontSize(4.cssRem)
  }
}

val SubheadlineTextStyle = CssStyle.base {
  Modifier
    .fontFamily("Montserrat", "sans-serif")
    .fontSize(1.2.cssRem)
    .fontWeight(FontWeight.Light)
    .textAlign(TextAlign.Center)
    .color(
      colorMode
        .toPalette()
        .color
        .toRgb()
        .copyf(alpha = 0.7f),
    ).letterSpacing(0.4.cssRem)
    .textTransform(TextTransform.Uppercase)
}

val SectionTextStyle = CssStyle.base {
  Modifier
    .fontFamily("Montserrat", "sans-serif")
    .fontSize(1.4.cssRem)
    .fontWeight(FontWeight.Light)
    .lineHeight(2)
    .opacity(0.8)
}

val CircleButtonVariant = ButtonStyle.addVariantBase {
  Modifier.padding(0.px).borderRadius(50.percent)
}

val UncoloredButtonVariant = ButtonStyle.addVariantBase {
  Modifier.setVariable(ButtonVars.BackgroundDefaultColor, Colors.Transparent)
}
